import type { UnwrapNestedRefs } from "vue";
import { defineComponent, onMounted, onUnmounted, ref, watch, h } from "vue";
import type { ComponentPublicInstance, ExtractPropTypes, PropType, Ref, SetupContext } from "vue";
import DataTable from "../../DataTable/src/index.vue";
import type { DataTablePropsType } from "../../DataTable/src/index.vue";
import QuickForm from "../../QuickForm/src";
import type { QuickFormPropsType } from "../../QuickForm/src";
import type { FormDataStateType, FormRenderItemConfType, QuickFormType, ResetFormInterface, SubmitFormInterface, ValidateInterface } from "../../QuickForm/src/types";
import "./style.scss";
import cloneDeep from "lodash-es/cloneDeep";
import { ElButton, ElCollapse, ElCollapseItem } from "element-plus";
import { buildProps } from "../../utils/props";
import { FetchTypeConf } from "./type";
import type { AfterDestroyedInterface, AfterMountedInterface, BaseListRefsType, BaseListSearchFormItemConfType, BaseListType, FetchApiInterface, FetchDataFilterType, FetchTypeConfKeyType, ListDataStateType, RenderNodesType, SearchBarWrapperInterface, ToggleRenderInterface, TopBtnRenderInterface, UrlFunctionType } from "./type";
import type { Method } from "axios";
import { reactive } from "vue";
import { isFunction } from "lodash-es";
import type { DataTableColumnType, DataTableType } from "../../DataTable/src/types";
import type { SubmitToSeverOperateType } from "../../commonMix/submitToServer/types";

export const makePageUrl = (url: string): any => {
  return (d: { pageNum: string | number; pageSize: string | number }) =>
    `${url}?page_num=${d.pageNum}&page_size=${d.pageSize}`;
};

export const makeFetchDataFilter = (
  fixParamsStringKeys: Array<string>
): unknown => {
  return (d: any) => {
    const res: any = {};
    delete d.pageNum;
    delete d.pageSize;
    fixParamsStringKeys.map((key) => {
      res[key] = !d[key] ? "" : d[key];
    });
    return res;
  };
};

const BaseListProps = {
  ...buildProps({
    /**
     * 配置请求实例,promise fetch对象
     */
    fetchApi: {
      type: [Boolean, Function as PropType<FetchApiInterface>],
      default: false,
    },
    /**
     * 配置查询的url
     */
    url: {
      type: [String, Function as PropType<UrlFunctionType>],
      default: "",
    },
    /**
     * 配置查询的方式
     */
    fetchType: {
      type: String as PropType<Method>,
      values: Object.keys(FetchTypeConf).map(
        (key) => FetchTypeConf[key as FetchTypeConfKeyType]
      ),
      default: FetchTypeConf.GET,
    },
    /**
     * 配置请求带的数据
     */
    fetchData: {
      type: [Object, Array],
      default: () => ({}),
    },
    /**
     * 配置查询的数据过滤方法
     */
    fetchDataFilter: {
      type: Function as PropType<FetchDataFilterType>,
      default: (res: unknown): unknown => res,
    },
    afterMounted: {
      // 当组件渲染完成时的回调
      type: Function as PropType<AfterMountedInterface>,
      default: (): void => {
        console.log("默认当组件渲染完成时的回调");
      },
    },
    afterDestroyed: {
      // 当组件注销时的回调
      type: Function as PropType<AfterDestroyedInterface>,
      default: (): void => {
        console.log("默认当组件注销时的回调");
      },
    },
    listPage: {
      // 是否是单独列表页
      type: Boolean,
      default: false,
    },

    tableStyle: {
      // 列表风格 normal,search-form-in-table
      type: String,
      default: "search-form-in-style",
    },

    searchToClearSelected: {
      // 搜索清除选中
      type: Boolean,
      default: true,
    },
    haveSearchForm: {
      // 是否含有搜索表单
      type: Boolean,
      default: true,
    },
    onlyFormItemStyle: {
      // 是否仅仅有搜索表单
      type: Boolean,
      default: true,
    },
    searchBarWrapper: {
      /*
        是否为baselist 的高级查询框样式，
        true为内置的样式
        false则还原默认样式,
        function则传入表单节点，和baselist实例最终renturn 为渲染的节点，
        或者使用scopedSlots searchBarWrapper 去自定义渲染
      */
      type: [Object, Boolean, Function as PropType<SearchBarWrapperInterface>],
      default: true,
    },

    defaultFetchData: {
      // 列表默认请求带的参数
      type: Object,
      default: function () {
        return {};
      },
    },
    tableAttrs: {
      // 直接设置到 组件内的 DataTable 组件的属性
      type: Object as PropType<Partial<DataTablePropsType>>,
      default: () => ({}),
    },
    // eslint-disable-next-line vue/require-default-prop
    selections: DataTable.props.selections,
    searchFormAttr: {
      // 直接设置到 组件内的 搜索框  QuickForm 组件的属性
      type: Object as PropType<Partial<QuickFormPropsType>>,

      default: () => ({}),
    },

    mergeFetchData: {
      // 会合并到 获取列表请求的参数
      type: Object,
      default: () => ({}),
    },
    searchFormRenderData: {
      // 高级查询的搜索表单渲染配置
      type: Array as PropType<FormRenderItemConfType<any>[]>,
      default: () => [],
    },
    tableHeader: {
      // table的头部渲染，跟element table参数对应
      type: Array as PropType<Array<DataTableColumnType>>,
      default: function () {
        return [];
      },
    },
    topBtnRender: {
      // table 的头部按钮组渲染
      type: [Boolean, Function as PropType<TopBtnRenderInterface>],
      default: false,
    },
    toggleSearchForm: {
      // table 的搜索表单是否为切换展示
      type: Boolean,
      default: false,
    },
    /**
     * 是否默认列表高度铺满屏幕
     */
    fullPageList: {
      type: Boolean,
      default: false,
    },
  }),
};

type BaseListPropsType = ExtractPropTypes<typeof BaseListProps>;

export default defineComponent({
  name: "BaseList",

  setup(props: BaseListPropsType, ctx: SetupContext<any>): BaseListType {
    let toggleSearchTime = -1;
    const listDataState: UnwrapNestedRefs<ListDataStateType> = reactive({
      showSearchForm: props.haveSearchForm,
      showSearchFormAll: false,

      searchForm: (props.searchFormAttr as any).modelValue?cloneDeep((props.searchFormAttr as any).modelValue):{},
      listPageTableHeight: false,
      mutipleSelection: cloneDeep(props.selections || []),
    });
    const refsObject: BaseListRefsType = {};

    const baseTableRef: any = (el: DataTableType) => {
      refsObject.baseTableObject = el;
    };

    const searchFormRef: any = (el: QuickFormType) => {
      refsObject.searchFormObject = el;
    };
    watch(
      () => props.haveSearchForm,
      (newVal: boolean) => {
        listDataState.showSearchForm = newVal;
      }
    );
    watch(
      () => listDataState.showSearchForm,
      () => {
        toggleSearchTime = new Date().valueOf();
        if (refsObject.baseTableObject) {
          refsObject.baseTableObject.setListFullPageHeight();
        }
      }
    );
    watch(
      () => listDataState.mutipleSelection,
      (newVal: any[]) => {
        ctx.emit("update:selections", newVal);
      }
    );
    watch(
      () => props.selections,
      (newVal: any[]) => {
        if (newVal != listDataState.mutipleSelection) {
          changeSelectRow(newVal);
        }
      }
    );
    onMounted(() => {
      ctx.emit("mountedHandler", ctx);
      if (isFunction(props.afterMounted)) props.afterMounted(ctx);
    });
    onUnmounted(() => {
      ctx.emit("unmountedHandler", ctx);
      if (isFunction(props.afterDestroyed)) props.afterDestroyed(ctx);
    });
    /**
     * 修改选中的列
     * @param {Array} 选中列表的数据 //
     */
    function changeSelectRow(newVal: any[]): void {
      listDataState.mutipleSelection = newVal;
    }

    /**
     * 当高级查询表单提交调用的函数
     * @param {Object}  // 表单提交的值
     */
    function onSearchSubmit(v: any): void {
       
      listDataState.searchForm = cloneDeep(v);
      // debugger
      if (props.searchToClearSelected) {
        listDataState.mutipleSelection = [];
      }
      refsObject.baseTableObject && refsObject.baseTableObject.lazyLoad(true); // 预防相同数据不重新请求
    }
    /**
     *
     * 重新刷新列表方法
     * @param {boolean} [reset=true] // 是否返回第一页
     */
    function reloadList(reset = true): void {
      if (props.searchToClearSelected) {
        listDataState.mutipleSelection = [];
      }
      refsObject.baseTableObject && refsObject.baseTableObject.loadData(reset);
    }

    function lightReloadList(reset = true): void {
      if (props.searchToClearSelected) {
        listDataState.mutipleSelection = [];
      }
      refsObject.baseTableObject && refsObject.baseTableObject.lightLoadData(reset);
    }
    /*
     * 清空高级查询刷新方法
     */
    function resetSearch(): void {
      if (props.searchToClearSelected) {
        listDataState.mutipleSelection = [];
      }
      
      refsObject.searchFormObject && refsObject.searchFormObject.resetForm();
   
      onSearchSubmit(refsObject.searchFormObject &&refsObject.searchFormObject.formDataState.formValue|| {});
      ctx.emit("searchFormClear");
    }
    function toggleSearchFormFunc(): void {
      listDataState.showSearchFormAll = !listDataState.showSearchFormAll;
    }
    function toggleSearchFormFilter(
      searchFormRenderData: FormRenderItemConfType<BaseListSearchFormItemConfType>[]
    ): FormRenderItemConfType<BaseListSearchFormItemConfType>[] {
      if (listDataState.showSearchFormAll) {
        // return searchFormRenderData;
        const resFormItems: FormRenderItemConfType<BaseListSearchFormItemConfType>[] =[];
        searchFormRenderData.map((o) => {
          if (o.toggleButton) {
            o.render = (v: any): RenderNodesType => {
              if (!o.toggleRender) {
                return null;
              }
              return o.toggleRender(
                v,
                toggleSearchFormFunc,
                listDataState.showSearchFormAll
              );
            };
          }
          resFormItems.push(o);
        });
        return resFormItems;
      } else {
        const resFormItems: FormRenderItemConfType<BaseListSearchFormItemConfType>[] =
          [];
        searchFormRenderData.map((o) => {
          if (
            o.fixedShow ||
            o.toggleButton ||
            listDataState.showSearchFormAll
          ) {
            if (o.toggleButton) {
              o.render = (v: any): RenderNodesType => {
                if (!o.toggleRender) {
                  return null;
                }
                return o.toggleRender(
                  v,
                  toggleSearchFormFunc,
                  listDataState.showSearchFormAll
                );
              };
            }
            resFormItems.push(o);
          }
        });
        return resFormItems;
      }
    }
    /**
     * 高级查询表单渲染方法
     */
    function searchBarWrapperRender(): RenderNodesType {
      const formProps = {
        labelPosition: "right",
        labelWidth: "100px",
      };

      const buttonGroupColConfig = {
        span: 24,
        style: {
          "text-align": "center",
          "margin-bottom": "15px",
        },
      };
      const _props=props;
      const setSearchFormAttrs: Partial<QuickFormPropsType> = {
        onSubmit: onSearchSubmit,
        formProps,
        renderData: props.toggleSearchForm
          ? toggleSearchFormFilter(props.searchFormRenderData)
          : props.searchFormRenderData,
        buttonGroupRender: (
          value: any,
          submitFunc: SubmitFormInterface,
          resetFunc: ResetFormInterface,
          validate: ValidateInterface,
          formDataState: UnwrapNestedRefs<FormDataStateType>,
          submitMixResult: SubmitToSeverOperateType,
          props: any
        ): RenderNodesType => {
          return _props.onlyFormItemStyle?[ ]: [
            <ElButton class="base-list-query-submit-btn" on-click={submitFunc}>查询</ElButton>,
            <ElButton class="base-list-clear-submit-btn" on-click={resetSearch}>清空</ElButton>,
          ];
        },
        buttonGroupColConfig,
        ...props.searchFormAttr,
      };
      const SearchFormVnode = (
        <quick-form
          ref={searchFormRef}
          {...{
            onlyFormItem: props.onlyFormItemStyle,
            buttonGroupRender:props.onlyFormItemStyle?()=>[]:setSearchFormAttrs.buttonGroupRender,
            ...setSearchFormAttrs,
          }}
        />
      );
      if (typeof props.searchBarWrapper == "function") {
        return props.searchBarWrapper(setSearchFormAttrs, SearchFormVnode,searchFormRef);
      } else if (
        typeof props.searchBarWrapper == "boolean" &&
        props.searchBarWrapper
      ) {
        return defaultSearchBarWrapper(setSearchFormAttrs);
      } else {
        return SearchFormVnode;
      }
    }

    function searchDataFunc():void{
      const setData:any={
        isDefaultSubmit:true,
        submitFunc:()=>{
          refsObject.searchFormObject && refsObject.searchFormObject.submitForm();
        }
      }
      ctx.emit("searchFormSubmit",setData);
      if(setData.isDefaultSubmit){
        setData.submitFunc();
      }
     
    }
    /**
     * 默认的高级查询表单外部框渲染方法
     * @param {Object}  // 设置表单的属性配置
     */
    function defaultSearchBarWrapper(
      setSearchFormAttrs: Partial<QuickFormPropsType>
    ): RenderNodesType {
      setSearchFormAttrs.buttonGroupColConfig = false;
      const SearchFormVnode = (
        <quick-form
          ref={searchFormRef}
          {...{
            onlyFormItem: props.onlyFormItemStyle,
            ...setSearchFormAttrs,
          }}
        />
      );
      return (
        <div class="base-list-searchbar-wrapper">
          <div class="base-list-searchbar-wrapper-btns">
            <ElButton
              onClick={() => {
                searchDataFunc();
          
              }}
              plain={true}
              type="primary"
              class="base-list-searchbar-wrapper-btn"
            >
              查询
            </ElButton>
            <ElButton
              onClick={() => {
                resetSearch();
              }}
              class="base-list-searchbar-wrapper-btn"
            >
              清空
            </ElButton>
          </div>
          {SearchFormVnode}
        </div>
      );
    }

    function searchFormWrap():any{
      return  props.haveSearchForm ? (
        <ElCollapse
          class="base-list-searchbar"
          modelValue={listDataState.showSearchForm ? "search-bar" : ""}
        >
          <ElCollapseItem name="search-bar">
            <div>
              <div ref="searchbarInbox" class="base-list-searchbar-inbox">
                {searchBarWrapperRender()}
              </div>
            </div>
          </ElCollapseItem>
        </ElCollapse>
      ) : null
    }
    function handleSearchWrapKeydown(event:any){
      if(event.code=="Enter"&&event.target.tagName=='INPUT'&&event.target.className.indexOf('el-input__inner')>=0){
        //  增加回车搜索
        
        event.target.blur();
        setTimeout(()=>{
          onSearchSubmit(refsObject.searchFormObject &&refsObject.searchFormObject.formDataState.formValue|| {});
        },800)
       
      }
    
    }
    function headerTopBlockRender():any{
      // 插入顶部方法
      if(props.tableStyle=='search-form-in-style'){
        // searchFormWrap()
        return [<div class="search-form-in-table-box"  onKeydown={handleSearchWrapKeydown}>{searchFormWrap()}</div>];
        // searchFormWrap()
      }else{
        return null;
      }
    }
    return {
      listDataState,
      searchForm:
        props.searchFormAttr && props.searchFormAttr.modelValue
          ? props.searchFormAttr.modelValue
          : {},
      searchBarWrapperRender,
      searchDataFunc,
      baseTableRef,
      refsObject,
      reloadList,
      lightReloadList,
      resetSearch,
      headerTopBlockRender,
      changeSelectRow,
      toggleSearchFormFunc,
      searchFormWrap,
      onSearchSubmit
    };
  },
  components: {
    DataTable,
    QuickForm,
    ElButton,
    ElCollapse,
    ElCollapseItem,
  },
  props: BaseListProps,
  render(_ctx: ComponentPublicInstance<BaseListPropsType, BaseListType>) {
    const props: BaseListPropsType = _ctx.$props;
    const { tableHeader, mergeFetchData, defaultFetchData } = props;
 
    return (
      <div class="base-list">
        {props.tableStyle=='normal'?_ctx.searchFormWrap():null}
        {props.topBtnRender || _ctx.$slots.topBtnRender ? <div ref="btnbar" class="base-list-btnbar">
          {!props.topBtnRender && _ctx.$slots.topBtnRender
            ? _ctx.$slots.topBtnRender({
                selectRows: _ctx.listDataState.mutipleSelection,
                tableObj: _ctx,
                search:_ctx.searchDataFunc,
                resetSearch:_ctx.resetSearch,
                toggleSearchFormAction:_ctx.toggleSearchFormFunc,
                formValue:_ctx.refsObject.searchFormObject && _ctx.refsObject.searchFormObject.formValue,
                showSearchFormAll:_ctx.listDataState.showSearchFormAll,
              })
            : null}
          {props.topBtnRender && isFunction(props.topBtnRender)
            ? props.topBtnRender(_ctx.listDataState.mutipleSelection, _ctx)
            : null}
        </div> : null}
        <div class="base-list-cont">
          {h(
            DataTable,
            {
              ref: (el: any) => _ctx.baseTableRef(el as DataTableType),

              ...{
                selections: _ctx.listDataState.mutipleSelection,
                "onUpdate:selections": ($$v: Array<any>): void => {
                  _ctx.listDataState.mutipleSelection = $$v;
                },
              },
              ...{
                searchToClearSelected:props.searchToClearSelected,
                fullPageList: props.fullPageList,
                url: props.url,
                fetchType: props.fetchType,
                fetchData: {
                  ...defaultFetchData,
                  ..._ctx.listDataState.searchForm,
                  ...mergeFetchData,
                  ...props.fetchData,
                },
                tableHeader,
                ...props.tableAttrs,
                fetchApi: props.fetchApi || undefined,
                fetchDataFilter:
                  props.tableAttrs.fetchDataFilter ||
                  props.fetchDataFilter ||
                  undefined,
                tableAttrs: {
                  border: true,
                  height: _ctx.listDataState.listPageTableHeight || undefined,
                  ...(props.tableAttrs.tableAttrs || {}),
                },
              },
            },
            {
              default:
                _ctx.$slots && _ctx.$slots.default
                  ? _ctx.$slots.default
                  : undefined,
              headerTopBlock: _ctx.headerTopBlockRender,
              tablePrepend: _ctx.$slots && _ctx.$slots.tablePrepend ? _ctx.$slots.tablePrepend : undefined,
              trPrepend: _ctx.$slots && _ctx.$slots.trPrepend
              ? _ctx.$slots.trPrepend : undefined,
              tbodyWrap:_ctx.$slots && _ctx.$slots.tbodyWrap? _ctx.$slots.tbodyWrap : undefined,
                 
            }
          )}
        </div>
      </div>
    );
  },
});
